import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { PaymentOption, PaymentOptionsError, PaymentOptionsStatus, TerminalErrorCode } from '../../model'
import { PaymentType } from '@afterpay/types'
import { PURGE } from 'redux-persist'
import { createDocuments, getPaymentOptions, updateCheckout, updatePaymentOption, updatePaymentOptions } from '../checkout/actions'
import { valueIsNotNullOrUndefined } from '@afterpay/utils'
import { ErrorCode } from '@afterpaytouch/portal-api'

export interface CreditFreezeRetryContext {
  deadline: string
  errorCode: ErrorCode
}

export interface ConsumerLendingState {
  selectedPaymentType?: PaymentType
  selectedPaymentOptionId?: number
  isAutopayEnabled?: boolean
  paymentOptions: PaymentOption[]
  paymentOptionsStatus: PaymentOptionsStatus
  loanAgreementDocumentId?: string
  paymentAuthorisationDocumentId?: string
  creditScoreDisclosureDocumentId?: string
  showEfaModal: boolean
  error?: PaymentOptionsError
  creditFreezeRetryContext?: CreditFreezeRetryContext
}

export const consumerLendingInitialState: ConsumerLendingState = {
  selectedPaymentType: undefined,
  selectedPaymentOptionId: undefined,
  isAutopayEnabled: true,
  paymentOptions: [],
  paymentOptionsStatus: PaymentOptionsStatus.Initial,
  loanAgreementDocumentId: undefined,
  paymentAuthorisationDocumentId: undefined,
  showEfaModal: false,
  error: undefined,
  creditFreezeRetryContext: undefined,
}

const slice = createSlice({
  name: 'consumerLending',
  initialState: consumerLendingInitialState,
  reducers: {
    setSelectedPaymentType(state: ConsumerLendingState, action: PayloadAction<PaymentType>) {
      state.selectedPaymentType = action.payload
    },
    setSelectedPaymentOptionId(state: ConsumerLendingState, action: PayloadAction<number>) {
      state.selectedPaymentOptionId = action.payload
    },
    setIsUpdatingCLAutopayEnabled(state: ConsumerLendingState, action: PayloadAction<boolean>) {
      state.isAutopayEnabled = action.payload
    },
    setPaymentOptionsError(state: ConsumerLendingState, action: PayloadAction<PaymentOptionsError>) {
      state.error = action.payload
      state.selectedPaymentType = PaymentType.PBI
      state.creditFreezeRetryContext = undefined
    },
    setShowEfaModal(state: ConsumerLendingState, action: PayloadAction<boolean>) {
      state.showEfaModal = action.payload
    },
    clearPaymentOptionsError(state: ConsumerLendingState) {
      state.error = undefined
      state.creditFreezeRetryContext = undefined
    },
    resetPaymentOptionsForRetry(state: ConsumerLendingState) {
      const priorError = state.error
      state.paymentOptionsStatus = PaymentOptionsStatus.Initial
      state.error = undefined
      state.selectedPaymentType = undefined
      const deadline = priorError?.extra?.creditFreezeApplicationDeadline
      if (priorError?.error === TerminalErrorCode.CreditFreezeDeclinedPCL && deadline != null && deadline !== '' && priorError.errorCode != null) {
        state.creditFreezeRetryContext = { deadline, errorCode: priorError.errorCode }
      }
    },
    hydratePaymentOptions(state: ConsumerLendingState, action: PayloadAction<PaymentOption[]>) {
      state.paymentOptions = action.payload
      state.paymentOptionsStatus = PaymentOptionsStatus.Fetched
      state.creditFreezeRetryContext = undefined
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getPaymentOptions.fulfilled, (state, action) => {
        state.paymentOptions = action.payload
        state.paymentOptionsStatus = PaymentOptionsStatus.Fetched
        state.creditFreezeRetryContext = undefined
      })
      .addCase(getPaymentOptions.pending, (state) => {
        state.paymentOptionsStatus = PaymentOptionsStatus.Fetching
      })
      .addCase(getPaymentOptions.rejected, (state) => {
        state.paymentOptionsStatus = PaymentOptionsStatus.Error
      })
      .addCase(updatePaymentOptions.fulfilled, (state, action) => {
        state.paymentOptions = action.payload
        state.paymentOptionsStatus = PaymentOptionsStatus.Fetched
      })
      .addCase(updatePaymentOptions.pending, (state) => {
        state.paymentOptionsStatus = PaymentOptionsStatus.Fetching
      })
      .addCase(updatePaymentOptions.rejected, (state) => {
        state.paymentOptionsStatus = PaymentOptionsStatus.Error
      })
      .addCase(updatePaymentOption.fulfilled, (state, action) => {
        state.selectedPaymentType = action.payload.paymentOption.paymentType
        state.selectedPaymentOptionId = action.payload.paymentOption.id
        state.paymentOptionsStatus = PaymentOptionsStatus.Fetched
      })
      .addCase(updatePaymentOption.pending, (state) => {
        state.paymentOptionsStatus = PaymentOptionsStatus.Updating
      })
      .addCase(updatePaymentOption.rejected, (state) => {
        state.paymentOptionsStatus = PaymentOptionsStatus.UpdateError
      })
      .addCase(createDocuments.fulfilled, (state, action) => {
        const response = action.payload
        state.loanAgreementDocumentId = response.LOAN_AGREEMENT
        state.paymentAuthorisationDocumentId = response.PAYMENT_AUTHORISATION
        state.creditScoreDisclosureDocumentId = response.CREDIT_SCORE_DISCLOSURE
      })
      .addCase(createDocuments.pending, (state) => {
        state.paymentOptionsStatus = PaymentOptionsStatus.Updating
      })
      .addCase(createDocuments.rejected, (state) => {
        state.paymentOptionsStatus = PaymentOptionsStatus.UpdateError
      })
      .addCase(updateCheckout.fulfilled, (state, action) => {
        const { paymentOptions } = action.payload
        if (valueIsNotNullOrUndefined(paymentOptions)) {
          state.paymentOptions = paymentOptions
        }
      })
      .addCase(PURGE, () => consumerLendingInitialState)
  },
})

export const {
  setSelectedPaymentType,
  setSelectedPaymentOptionId,
  setIsUpdatingCLAutopayEnabled,
  setShowEfaModal,
  setPaymentOptionsError,
  clearPaymentOptionsError,
  resetPaymentOptionsForRetry,
  hydratePaymentOptions,
} = slice.actions

export default slice.reducer
